// Server-side state, faked. The mock API keeps its "database" here so a
// refresh (or a second tab) behaves like a real backend session instead of
// resetting to a pristine happy path.

const KEY = "sfMockSession.v1";

export interface IMockSession {
  sessionId: string;
  firstName: string;
  lastName: string;
  // Made by step 1's "Continue as" (the classic site's actor): an existing account, not a new signup.
  existingAccount?: boolean;
  email: string;
  breakdownId: string | null;
  roleId: string | null;
  geoCountryCode?: string;
  geoCityId?: number;
  geoRegionCode?: string;
  // Set by PATCH home-location when the visitor corrects the auto-picked
  // country/city from the country gate.
  homeCountryCode?: string;
  homeCityId?: number;
  // How verification placed the account (resolveAccountCity's rung), and
  // whether the visitor has chosen their film center since.
  geoSource?: string;
  homeConfirmed?: boolean;

  // email verification
  code: string;
  codeExpiresOnUtc: string;
  resendAvailableAtUtc: string;
  attemptsRemaining: number;
  sendsRemaining: number;
  verified: boolean;

  // account (exists only after verification)
  clientId: number | null;
  jwt: string | null;

  // step 2
  submissionId: number | null;
  photoAssetId: number | null;
  resumeAssetId: number | null;
  note: string;
  ageAttested: boolean;

  // step 3
  phone: string | null;
  phoneCode: string | null;
  phoneCodeExpiresOnUtc: string | null;
  phoneResendAvailableAtUtc: string | null;
  phoneAttemptsRemaining: number;
  phoneSendsRemaining: number;
  phoneVerified: boolean;

  // step 4
  // Step 4's job-alerts checkbox: unset until chosen (shown ticked), true ticked, false unticked.
  jobAlerts?: boolean;
  // What the membership check answers with. A new account is "free" until a
  // checkout completes; the ?mockScenario= knobs start it somewhere else.
  membershipStatus: "free" | "trial" | "pro";
  trialUsed: boolean;
  serviceId: number | null;
  checkoutSessionId: string | null;
  attemptId: number | null;
  // How many times the status of the CURRENT session has been polled — the
  // mock answers "open" twice before "complete" so the return page's polling
  // is exercised rather than short-circuited.
  checkoutPolls: number;
  checkoutStatus: "open" | "complete" | "expired" | null;
  representationType: "selfRepresented" | "agency" | "invited";
  representationAgencyId: number | null;
  representationAgencyName: string | null;
  representationAgentName: string | null;
  representationInvitedEmail: string | null;
  submissionStatus: "Draft" | "Saved" | "Sent";
  holdUntilUtc: string | null;
  reminderScheduledOnUtc: string | null;
  sentOnUtc: string | null;
  // The idempotency key of the send that succeeded, so a replay is recognised
  // rather than creating a second submission.
  sentClientRequestId: string | null;
  // A/B: which arm was shown, and whether the backend has been told.
  step4Variant: "a" | "b" | "c" | "d" | null;
  exposureRecorded: boolean;
  // The submission a checkout started from (the real one's A/B record).
  checkoutStartedFrom?: number | null;
  // The last finish-later email (docs/31): one a day, as the real one.
  finishLaterEmailAt?: string | null;
}

// Fixed code so demos are reproducible; the legacy backend has the same idea
// with its 676767 test code.
export const MOCK_EMAIL_CODE = "123456";
// 4 digits, like the real SMS code (the server's phoneCodeLength).
export const MOCK_PHONE_CODE = "6767";

export const MAX_VERIFY_ATTEMPTS = 4;
export const MAX_SENDS = 4;
export const EMAIL_CODE_TTL_MIN = 90;
export const PHONE_CODE_TTL_MIN = 30;
export const EMAIL_RESEND_COOLDOWN_S = 30;
export const PHONE_RESEND_COOLDOWN_S = 60;

const minutesFromNow = (min: number) =>
  new Date(Date.now() + min * 60 * 1000).toISOString();

const secondsFromNow = (sec: number) =>
  new Date(Date.now() + sec * 1000).toISOString();

export const readMockSession = (): IMockSession | null => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as IMockSession) : null;
  } catch (e) {
    return null;
  }
};

export const writeMockSession = (session: IMockSession): IMockSession => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch (e) {
    // storage unavailable — the flow still works within the page
  }
  return session;
};

export const clearMockSession = () => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // ignore
  }
};

export const createMockSession = (payload: {
  firstName: string;
  lastName: string;
  email: string;
  breakdownId?: string;
  roleId?: string;
  geoCountryCode?: string;
  geoCityId?: number;
  geoRegionCode?: string;
}): IMockSession =>
  writeMockSession({
    sessionId: `mock-${Date.now().toString(36)}`,
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    breakdownId: payload.breakdownId || null,
    roleId: payload.roleId || null,
    geoCountryCode: payload.geoCountryCode,
    geoCityId: payload.geoCityId,
    geoRegionCode: payload.geoRegionCode,

    code: MOCK_EMAIL_CODE,
    codeExpiresOnUtc: minutesFromNow(EMAIL_CODE_TTL_MIN),
    resendAvailableAtUtc: secondsFromNow(EMAIL_RESEND_COOLDOWN_S),
    attemptsRemaining: MAX_VERIFY_ATTEMPTS,
    sendsRemaining: MAX_SENDS,
    verified: false,

    clientId: null,
    jwt: null,

    submissionId: null,
    photoAssetId: null,
    resumeAssetId: null,
    note: "",
    ageAttested: false,

    phone: null,
    phoneCode: null,
    phoneCodeExpiresOnUtc: null,
    phoneResendAvailableAtUtc: null,
    phoneAttemptsRemaining: MAX_VERIFY_ATTEMPTS,
    phoneSendsRemaining: MAX_SENDS,
    phoneVerified: false,

    // step 4. Every field is initialised here as well as declared above — an
    // omission in this object is invisible to the type checker (the interface
    // is satisfied by the literal) but shows up later as a session that has
    // forgotten something.
    membershipStatus: "free",
    trialUsed: false,
    serviceId: null,
    checkoutSessionId: null,
    attemptId: null,
    checkoutPolls: 0,
    checkoutStatus: null,
    // Set at account creation from the account defaults, which pick
    // self-represented — the flow never asks before the review screen.
    representationType: "selfRepresented",
    representationAgencyId: null,
    representationAgencyName: null,
    representationAgentName: null,
    representationInvitedEmail: null,
    submissionStatus: "Draft",
    holdUntilUtc: null,
    reminderScheduledOnUtc: null,
    sentOnUtc: null,
    sentClientRequestId: null,
    step4Variant: null,
    exposureRecorded: false,
    checkoutStartedFrom: null,
  });

export const maskEmail = (email: string): string => {
  const at = email.indexOf("@");
  if (at < 1) return email;
  const name = email.slice(0, at);
  const domain = email.slice(at);
  if (name.length <= 2) return `${name[0]}***${domain}`;
  return `${name.slice(0, 2)}***${domain}`;
};

export const maskPhone = (phone: string): string =>
  phone.length <= 4 ? phone : `••• ••• ${phone.slice(-3)}`;

export const isoIn = { minutes: minutesFromNow, seconds: secondsFromNow };
